import React, { useState } from "react";
import { FaTrophy } from "react-icons/fa";
import { IoChevronDown } from "react-icons/io5";

const getScoreClass = (score) => {
  if (!score || score === "E" || score === "--") return "text-gray-900";
  const num = parseInt(score);
  if (isNaN(num)) return "text-gray-900";
  return num < 0 ? "text-red-600" : num > 0 ? "text-blue-600" : "text-gray-900";
};

const formatScore = (score) => {
  if (!score || score === "--") return "--";
  if (score === "E" || score === "0") return "E";
  const num = parseInt(score);
  if (isNaN(num)) return score;
  return num > 0 ? `+${num}` : `${num}`;
};

const abbreviateName = (name) => {
  if (!name) return "Empty";
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
};

const formatPlayerStatus = (status, holesPlayed, teeTime) => {
  const s = status?.toLowerCase();
  if (s === "active") {
    return holesPlayed && holesPlayed !== "--" ? `Thru ${holesPlayed}` : "Active";
  }
  if (s === "not started") {
    return teeTime
      ? new Date(teeTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : "--";
  }
  if (s === "complete") {
    if (teeTime && new Date(teeTime) > new Date()) {
      return new Date(teeTime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    return "F";
  }
  return status || "--";
};

function TeamTable({ teams, teamLineItems, canViewTeam, isTournamentStarted, tieBreaker }) {
  const [expandedRows, setExpandedRows] = useState({});

  const sortedTeams = React.useMemo(() => {
    const currentUserId = Number(localStorage.getItem("userId"));

    const parseScore = (s) => {
      if (s === null || s === undefined || s === "--") return Infinity;
      if (String(s).toUpperCase() === "E") return 0;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? Infinity : n;
    };

    const parseRank = (rank) => {
      if (!rank) return Infinity;
      const str = rank.toString().toUpperCase();
      if (str === "MC") return Infinity;
      const num = parseInt(str.replace("T", ""), 10);
      return isNaN(num) ? Infinity : num;
    };

    const tieBreakerCompare = (a, b) => {
      if (tieBreaker === "Average World Ranking") {
        const aWR = parseFloat(a.team_world_ranking_average);
        const bWR = parseFloat(b.team_world_ranking_average);
        const aVal = Number.isNaN(aWR) ? 999999 : aWR;
        const bVal = Number.isNaN(bWR) ? 999999 : bWR;
        if (aVal !== bVal) return aVal - bVal;
      } else if (tieBreaker === "Winning Score") {
        const aDiff = parseFloat(a.tie_breaker_difference);
        const bDiff = parseFloat(b.tie_breaker_difference);
        const aVal = Number.isNaN(aDiff) ? 999999 : aDiff;
        const bVal = Number.isNaN(bDiff) ? 999999 : bDiff;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    };

    return [...teams].sort((a, b) => {
      const isMCA = a.rank?.toString().toUpperCase() === "MC";
      const isMCB = b.rank?.toString().toUpperCase() === "MC";
      if (isMCA && !isMCB) return 1;
      if (!isMCA && isMCB) return -1;

      // Both MC: score-to-par first, then pool's tie-breaker, then total strokes
      if (isMCA && isMCB) {
        const scoreA = parseScore(a.team_score_to_par);
        const scoreB = parseScore(b.team_score_to_par);
        if (scoreA !== scoreB) return scoreA - scoreB;
        const tb = tieBreakerCompare(a, b);
        if (tb !== 0) return tb;
        const totalA = parseScore(a.team_score_total);
        const totalB = parseScore(b.team_score_total);
        return totalA - totalB;
      }

      const rankA = parseRank(a.rank);
      const rankB = parseRank(b.rank);
      if (rankA !== rankB) return rankA - rankB;

      // Tied eligible teams: pre-tournament puts not-started teams below
      const aNotStarted = a.team_score_total === "0" || a.team_score_total === 0;
      const bNotStarted = b.team_score_total === "0" || b.team_score_total === 0;
      if (aNotStarted && !bNotStarted) return 1;
      if (!aNotStarted && bNotStarted) return -1;
      if (aNotStarted && bNotStarted) {
        const isUserA = (a.ownerDetails?.id || a.owner) === currentUserId;
        const isUserB = (b.ownerDetails?.id || b.owner) === currentUserId;
        if (isUserA) return -1;
        if (isUserB) return 1;
      }

      // In-play tie: apply the pool's configured tie-breaker
      const tb = tieBreakerCompare(a, b);
      if (tb !== 0) return tb;
      return 0;
    });
  }, [teams, tieBreaker]);

  const toggleRow = (teamId) => {
    setExpandedRows((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  return (
    <div className="space-y-2 p-3">
      {sortedTeams.map((team) => {
        const isExpanded = expandedRows[team.id];
        const isViewable = canViewTeam(team);
        const isWinner = team.pool?.tournament_status === "Official" && team.rank === "1";
        const isCut = team.rank?.toString().toUpperCase() === "MC";
        const teamName = team.player_name || team.ownerDetails?.full_name || "Unknown";
        const teamItems = teamLineItems.filter((item) => item.team === team.id);

        return (
          <div key={team.id}>
            {/* Team Row */}
            <div
              className={`flex items-center px-4 py-3.5 rounded-xl transition-all ${
                isWinner ? "bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200" :
                isCut ? "bg-red-50/60 border border-red-100" :
                isExpanded ? "bg-primary/5 border border-primary/20" :
                "bg-gray-50 border border-gray-100 hover:border-gray-200"
              } ${isViewable ? "cursor-pointer" : ""}`}
              onClick={() => isViewable && toggleRow(team.id)}
            >
              {/* Rank */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mr-4 ${
                isWinner ? "bg-yellow-400 text-white shadow-sm" :
                isCut ? "bg-red-100 text-red-600" :
                "bg-white text-gray-700 shadow-sm border border-gray-200"
              }`}>
                {isCut ? "MC" : team.rank || "--"}
              </div>

              {/* Name + Stats */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-gray-900 truncate">{teamName}</p>
                  {isWinner && <FaTrophy className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                </div>
                {isViewable && (
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    {team.team_world_ranking_average && team.team_world_ranking_average !== "--" && (
                      <span>Avg WR: {parseFloat(team.team_world_ranking_average).toFixed(1)}</span>
                    )}
                    {team.team_score_total && team.team_score_total !== "--" && team.team_score_total !== "0" && (
                      <span>Score: {team.team_score_total}</span>
                    )}
                  </div>
                )}
                {!isViewable && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {isTournamentStarted ? "View restricted" : "Visible when tournament begins"}
                  </p>
                )}
              </div>

              {/* To Par + Chevron */}
              <div className="flex items-center gap-3 ml-4">
                {isViewable && (
                  <p className={`text-lg font-bold ${getScoreClass(team.team_score_to_par)}`}>
                    {formatScore(team.team_score_to_par)}
                  </p>
                )}
                {isViewable && teamItems.length > 0 && (
                  <IoChevronDown
                    className="w-5 h-5 text-gray-400 transition-transform duration-200"
                    style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(90deg)" }}
                  />
                )}
              </div>
            </div>

            {/* Expanded Player Details */}
            {isExpanded && isViewable && teamItems.length > 0 && (() => {
              const sortedItems = [...teamItems].sort((a, b) => {
                const scoreA = a.total_score_to_par === "E" ? 0 : parseInt(a.total_score_to_par) || 999;
                const scoreB = b.total_score_to_par === "E" ? 0 : parseInt(b.total_score_to_par) || 999;
                return scoreA - scoreB;
              });

              return (
                <div className="mt-1 bg-white rounded-xl border border-gray-200 overflow-hidden mx-2">
                  {/* Desktop Table */}
                  <table className="hidden md:table w-full table-fixed">
                    <colgroup>
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "9%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "12%" }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-200">
                        {["#", "Player", "WR", "Pos", "R1", "R2", "R3", "R4", "To Par", "Status"].map((h) => (
                          <th key={h} className={`text-[10px] font-semibold text-gray-400 uppercase tracking-wider py-2.5 px-2 ${h === "Player" ? "text-left" : "text-center"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((item, i) => (
                        <tr key={item.id} className={`text-sm border-b border-gray-100 last:border-0 ${i % 2 === 1 ? "bg-gray-50/50" : ""}`}>
                          <td className="text-center text-gray-400 text-xs py-2.5 px-2">{i + 1}</td>
                          <td className="font-medium text-gray-900 truncate py-2.5 px-2">{item.player?.player_name || "Empty"}</td>
                          <td className="text-center text-xs text-gray-500 py-2.5 px-2">{item.player?.world_ranking_position || "--"}</td>
                          <td className="text-center text-xs text-gray-600 font-medium py-2.5 px-2">{item.tournament_position || "--"}</td>
                          <td className={`text-center text-xs whitespace-nowrap py-2.5 px-2 ${getScoreClass(item.round_1_to_par)}`}>{formatScore(item.round_1_to_par)}</td>
                          <td className={`text-center text-xs whitespace-nowrap py-2.5 px-2 ${getScoreClass(item.round_2_to_par)}`}>{formatScore(item.round_2_to_par)}</td>
                          <td className={`text-center text-xs whitespace-nowrap py-2.5 px-2 ${getScoreClass(item.round_3_to_par)}`}>{formatScore(item.round_3_to_par)}</td>
                          <td className={`text-center text-xs whitespace-nowrap py-2.5 px-2 ${getScoreClass(item.round_4_to_par)}`}>{formatScore(item.round_4_to_par)}</td>
                          <td className={`text-center text-xs font-semibold whitespace-nowrap py-2.5 px-2 ${getScoreClass(item.total_score_to_par)}`}>{formatScore(item.total_score_to_par)}</td>
                          <td className="text-center text-xs text-gray-500 whitespace-nowrap py-2.5 px-2">{formatPlayerStatus(item.players_tournament_status, item.holes_played, item.tee_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile Table */}
                  <table className="md:hidden w-full table-fixed">
                    <colgroup>
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "36%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "22%" }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-200">
                        {["#", "Player", "Pos", "To Par", "Status"].map((h) => (
                          <th key={h} className={`text-[10px] font-semibold text-gray-400 uppercase tracking-wider py-2.5 px-1.5 ${h === "Player" ? "text-left" : "text-center"}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((item, i) => (
                        <tr key={item.id} className={`text-sm border-b border-gray-100 last:border-0 ${i % 2 === 1 ? "bg-gray-50/50" : ""}`}>
                          <td className="text-center text-gray-400 text-xs py-2.5 px-1.5">{i + 1}</td>
                          <td className="font-medium text-gray-900 truncate py-2.5 px-1.5 text-xs">{abbreviateName(item.player?.player_name)}</td>
                          <td className="text-center text-xs text-gray-600 font-medium py-2.5 px-1.5">{item.tournament_position || "--"}</td>
                          <td className={`text-center text-xs font-semibold py-2.5 px-1.5 ${getScoreClass(item.total_score_to_par)}`}>{formatScore(item.total_score_to_par)}</td>
                          <td className="text-center text-xs text-gray-500 py-2.5 px-1.5">{formatPlayerStatus(item.players_tournament_status, item.holes_played, item.tee_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

export default React.memo(TeamTable);
